import { directSupabase } from '../lib/supabaseClient';
import { documentsService } from '../modules/docs/services/documents.service';
import type { Document } from '../modules/docs/types/documents.types';
import { isDocumentUnassigned } from './documentAssignment';

export interface DocumentLink {
  id: string;
  projectId: string;
  documentReference: string;
  documentType: 'Estimate' | 'Quotation' | 'Invoice';
  source: 'jarvis';
  createdAt: string;
}

export interface LinkableDocument {
  id: string;
  reference: string;
  type: 'Estimate' | 'Quotation' | 'Invoice';
  source: 'jarvis';
  title: string;
  customerName: string;
  amount: number;
  status: string;
  createdAt: string;
  projectId: string | null;
}

class DocumentLinkingService {
  /**
   * Get all linkable documents (JARVIS only)
   */
  async getAllLinkableDocuments(): Promise<LinkableDocument[]> {
    try {
      // JARVIS only
      const jarvisDocuments = await this.getJarvisDocuments();
      return jarvisDocuments.sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    } catch (error) {
      console.error('Error fetching linkable documents:', error);
      throw error;
    }
  }

  /**
   * Get QuoteDock documents for linking
   */
  private async getQuoteDockDocuments(): Promise<LinkableDocument[]> { return []; }

  /**
   * Get JARVIS documents for linking
   */
  private async getJarvisDocuments(): Promise<LinkableDocument[]> {
    try {
      const documents = await documentsService.getAllDocumentsForLinking();

      // Include estimates, quotes, and invoices
      const filteredDocuments = documents.filter(
        (doc) => doc.kind === 'Estimate' || doc.kind === 'Quotation' || doc.kind === 'Invoice'
      );

      // Prefer active version header titles for versioned docs (Estimate/Quotation).
      // The base documents table can retain the v1 title even when active version changes.
      const activeVersionIds = Array.from(new Set(
        filteredDocuments
          .map(d => d.activeVersionId)
          .filter((id): id is string => typeof id === 'string' && !!id.trim())
      ));
      const activeTitleByVersionId = new Map<string, string>();
      if (activeVersionIds.length > 0) {
        try {
          const { data, error } = await directSupabase
            .from('document_versions')
            .select('id,title')
            .in('id', activeVersionIds);
          if (!error && Array.isArray(data)) {
            for (const r of data as any[]) {
              const id = String(r?.id || '').trim();
              const title = String(r?.title || '').trim();
              if (id && title) activeTitleByVersionId.set(id, title);
            }
          }
        } catch (e) {
          // ignore: title enrichment should never block linking UI
          console.warn('Failed to load active version titles for linking', e);
        }
      }
      const bestTitle = (doc: Document): string => {
        if ((doc.kind === 'Estimate' || doc.kind === 'Quotation') && doc.activeVersionId) {
          return activeTitleByVersionId.get(doc.activeVersionId) || doc.title || 'Untitled Document';
        }
        return doc.title || 'Untitled Document';
      };

      return filteredDocuments
        .filter((doc) => isDocumentUnassigned(doc.projectId))
        .map((doc) => {
          const customerName =
            doc.customerFirstName && doc.customerLastName
              ? `${doc.customerFirstName} ${doc.customerLastName}`.trim()
              : doc.customerFirstName || doc.customerLastName || 'No Customer Assigned';
          return {
            id: doc.id,
            reference: doc.kind === 'Invoice' && doc.invoiceRef ? doc.invoiceRef : doc.lifecycleRef,
            type: doc.kind as 'Estimate' | 'Quotation' | 'Invoice',
            source: 'jarvis' as const,
            title: bestTitle(doc),
            customerName,
            amount: Number(doc.grossTotal ?? 0),
            status: doc.status,
            createdAt: doc.createdAt,
            projectId: doc.projectId ?? null,
          } as LinkableDocument;
        });
    } catch (error) {
      console.error('Error fetching JARVIS documents:', error);
      return [];
    }
  }

  /**
   * Create a link for a document (JARVIS only)
   */
  async createLink(projectId: string, documentReference: string, documentType: 'Estimate' | 'Quotation' | 'Invoice', source: 'jarvis'): Promise<void> {
    try {
      if (source === 'jarvis') {
        let lookup = directSupabase
          .from('documents')
          .select('id, kind')
          .eq('kind', documentType)
          .limit(1);
        lookup = documentType === 'Invoice'
          ? lookup.eq('invoice_ref', documentReference)
          : lookup.eq('lifecycle_ref', documentReference);
        const { data, error: lookupError } = await lookup;
        if (lookupError) throw lookupError;
        let documentId = Array.isArray(data) ? data[0]?.id : (data as { id?: string } | null)?.id;
        if (!documentId && documentType === 'Invoice') {
          const fallback = await directSupabase
            .from('documents')
            .select('id, kind')
            .eq('kind', documentType)
            .eq('lifecycle_ref', documentReference)
            .limit(1);
          if (fallback.error) throw fallback.error;
          documentId = Array.isArray(fallback.data) ? fallback.data[0]?.id : (fallback.data as { id?: string } | null)?.id;
        }
        if (!documentId) throw new Error('Document not found');
        await documentsService.updateDocument(documentId, { projectId });
      }
    } catch (error) {
      console.error('Error creating document link:', error);
      throw error;
    }
  }

  /**
   * Get all links for a project
   */
  async getProjectLinks(projectId: string): Promise<DocumentLink[]> {
    try {
      // Get JARVIS documents linked to this project
      const jarvisDocuments = await documentsService.getDocumentsByProject(projectId);
      
      const links: DocumentLink[] = [];

      // Add JARVIS links
      jarvisDocuments.forEach(doc => {
        links.push({
          id: doc.id,
          projectId: projectId,
          documentReference: doc.kind === 'Invoice' && doc.invoiceRef ? doc.invoiceRef : doc.lifecycleRef,
          documentType: doc.kind as 'Estimate' | 'Quotation' | 'Invoice',
          source: 'jarvis',
          createdAt: doc.createdAt
        });
      });

      return links;
    } catch (error) {
      console.error('Error fetching project links:', error);
      throw error;
    }
  }

  /**
   * Delete a link (JARVIS only)
   */
  async deleteLink(linkId: string, source: 'jarvis'): Promise<void> {
    try {
      if (source === 'jarvis') {
        // For JARVIS documents, we need to remove the project_id
        const { error } = await directSupabase
          .from('documents')
          .update({ project_id: null })
          .eq('id', linkId);

        if (error) {
          console.error('Error unlinking JARVIS document:', error);
          throw error;
        }
      }
    } catch (error) {
      console.error('Error deleting document link:', error);
      throw error;
    }
  }
}

export const documentLinkingService = new DocumentLinkingService();
